import React, { FC, Suspense, useContext, useEffect, useState } from "react";
import { CancelTokenSource } from "axios";
import { Location } from "history";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { TFunction, useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation } from "react-router";
import { Link } from "react-router-dom";
import { apiOrganization } from "src/services/Api/Organization/apiOrganization";
import { wrapPromise } from "go-core";
import useFlash from "go-alert/AlertMessage";
import Header, { ButtonProps } from "go-app/components/Header";
import { MobileActionProps } from "go-app/components/MobileActions/MobileAction";
import handleError from "go-app/services/errors";
import { ReactComponent as InfoAlertSVG } from "go-component/images/svg/info-circle.svg";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import { LoadingContainer } from "go-core/components/Loading";
import RenderLimitedText from "go-core/components/RenderLimitedText";
import { useWindowSize } from "go-core/components/useWindowSize";
import { FILTER_VALUE_SEPARATOR } from "go-list/core/components/Filter/services";
import { listFiltersDecode } from "go-list/core/components/Filter/services/decoder";
import { CustomFieldInsertPosition, ListConfigField } from "go-list/core/services/types";
import { ListData } from "go-list/list";
import { getSelectedSegmentForListConfig } from "go-list/list/services/segment-service";
import { GoListSegmentType, ListConfig } from "go-list/list/services/types";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { OrganizationState } from "go-security/services/organizations/types";
import { hasRole } from "go-security/services/users/selectors";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext, SegmentListService } from "go-segment/context";
import { StockApi, StockStateApi } from "../../../../../../../../services/Api/Organization/types";

interface ListState {
	resource?: Record<string, any>;
	stocks: StockApi[];
	handleCorrectStates(): Promise<void>;
}

const listName = "STOCK_PRODUCT_STATES";
const resourceType = "STOCK_STATES";

const BELOW_ZERO_COLOR = "#FFE1E4";
const BELOW_CRITICAL_AMOUNT_COLOR = "#FFE4BB";
const BELOW_OPTIMAL_AMOUNT_COLOR = "#FFF8D8";

const buildConfig = (
	t: TFunction,
	data: Record<string, any>,
	location: Location,
	segmentListService: SegmentListService,
	stocks: StockApi[],
	organization: OrganizationState
) => {
	const getStockStateSummary = (item: StockStateApi, stock?: StockApi) => {
		let inStockAmount, commitedAmount, incomingAmount;

		if (stock) {
			const searchingState = item.state.find((state) => state.stock.id === stock.id);

			if (!searchingState?.product_state)
				return {
					summary: t("modules.stock_state.field.inaccessible.title"),
					asLink: false,
				};
			const {
				product_state: { in_stock_amount, commited_amount, incoming_amount },
			} = searchingState;
			inStockAmount = in_stock_amount;
			commitedAmount = commited_amount;
			incomingAmount = incoming_amount;
		} else {
			const { in_stock_amount, commited_amount, incoming_amount } = item.summary;
			inStockAmount = in_stock_amount;
			commitedAmount = commited_amount;
			incomingAmount = incoming_amount;
		}

		const measureType = t(`enums.products.measure_types.${item.product.measure_type}`);

		return {
			summary: `${inStockAmount}${measureType} / ${commitedAmount}${measureType} / ${incomingAmount}${measureType}`,
			asLink: true,
		};
	};

	const defaultColumns = [
		{
			id: "name",
			name: t("common.word.product"),
			render: (item: StockStateApi) => (
				<Link rel={"noreferrer"} target={"_blank"} to={`/${organization.id}/products/${item.product.id}/edit`}>
					{item.product.name}
				</Link>
			),
			renderExport: (item: StockStateApi) => item.product.name,
		},
		{
			id: "category",
			name: t("common.word.category"),
			render: (item: StockStateApi) => (
				<RenderLimitedText minWidth={200}>{item?.product?.category?.name}</RenderLimitedText>
			),
			renderExport: (item: StockStateApi) => item?.product?.category?.name,
		},
		{
			id: "in_stock_amount",
			name: t("lib:common.word.summary"),
			render: (item: StockStateApi) => (
				<Link
					rel={"noreferrer"}
					target={"_blank"}
					to={`/${organization.id}/products/${item.product.id}/edit`}
					title={t("modules.stock_state.field.stock_state.tooltip")}
				>
					{getStockStateSummary(item).summary}
				</Link>
			),
			renderExport: (item: StockStateApi) => getStockStateSummary(item).summary,
			cellStyleOverride: (item: StockStateApi) => ({
				backgroundColor: getTableRowColor(item),
				textAlign: "left",
			}),
			headerNameAfter: () => (
				<OverlayTrigger
					placement="top"
					overlay={<Tooltip>{t("modules.stock_state.field.stock_state.tooltip")}</Tooltip>}
				>
					<InfoAlertSVG className="ms-1" onClick={(event) => event.stopPropagation()} />
				</OverlayTrigger>
			),
		},
	];

	const getStockColumns = (stockColumns: StockApi[]) =>
		stockColumns.map(
			(stock) =>
				({
					id: stock.id.toString(),
					name: stock.name,
					disableSorting: true,
					render: (item: StockStateApi) => {
						const { summary, asLink } = getStockStateSummary(item, stock);
						return asLink ? (
							<Link
								rel={"noreferrer"}
								target={"_blank"}
								to={`/${organization.id}/products/${item.product.id}/edit`}
								title={t("modules.stock_state.field.stock_state.tooltip")}
							>
								{summary}
							</Link>
						) : (
							<span>{summary}</span>
						);
					},
					renderExport: (item: StockStateApi) => getStockStateSummary(item, stock).summary,
					cellStyleOverride: (item: StockStateApi) => ({
						backgroundColor: getTableRowColor(item),
						textAlign: "left",
					}),
					headerStyleOverride: {
						textAlign: "left",
					},
					insertPosition: CustomFieldInsertPosition.ALPHABETICAL,
					exportType: "number",
				} as ListConfigField)
		);

	const getTableRowColor = (item: StockStateApi) => {
		const inStockAmount = item.summary.in_stock_amount;
		const criticalAmountLevel = item.product.critical_amount_level;
		const optimalAmountLevel = item.product.optimal_amount_level;

		if (inStockAmount < 0) return BELOW_ZERO_COLOR;
		if (inStockAmount < Number(criticalAmountLevel)) return BELOW_CRITICAL_AMOUNT_COLOR;
		if (inStockAmount < Number(optimalAmountLevel)) return BELOW_OPTIMAL_AMOUNT_COLOR;
		return "";
	};

	let config = {
		fields: [...defaultColumns, ...getStockColumns(stocks)],
		selectedColumns: ["name", "category", "in_stock_amount", ...stocks.map((col) => col.id.toString())],
		filters: [
			{
				id: "productIds",
				name: t("common.word.product"),
				type: "search_select",
				source: {
					request: (search: string, params: Record<string, any>, options?: Record<string, any>) => {
						const newParams = { ...params, "product_type|e": `BASIC${FILTER_VALUE_SEPARATOR}COMPOSITE` };

						return apiOrganization.getProductSearchSelect(search, newParams, {
							cancelToken: options?.token,
						});
					},
				},
				hasDefaultOptions: false,
			},
			{
				id: "categoryIds",
				name: t("common.word.category"),
				type: "search_select",
				source: {
					request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						apiOrganization.getProductCategorySearchSelect(search, params, { cancelToken: options?.token }),
				},
				hasDefaultOptions: false,
			},
			{
				id: "stockIds",
				name: t("common.word.stock"),
				type: "search_select",
				source: {
					request: (t: string, obj: Record<string, any>, options?: Record<string, any>) =>
						apiOrganization.getStockSearchSelect(t, obj, { cancelToken: options?.token }),
				},
			},
			{
				id: "critical_amount",
				name: t("modules.stock_state.field.critical_amount.title"),
				type: "list",
				listOptions: {
					in_stock: t("enums.stocks.product_state_amount.IN_STOCK"),
					available_stock: t("enums.stocks.product_state_amount.AVAILABLE_STOCK"),
					available_stock_with_incoming: t("enums.stocks.product_state_amount.AVAILABLE_STOCK_WITH_INCOMING"),
				},
				hasDefaultOptions: false,
			},
			{
				id: "optimal_amount",
				name: t("modules.stock_state.field.optimal_amount.title"),
				type: "list",
				listOptions: {
					in_stock: t("enums.stocks.product_state_amount.IN_STOCK"),
					available_stock: t("enums.stocks.product_state_amount.AVAILABLE_STOCK"),
					available_stock_with_incoming: t("enums.stocks.product_state_amount.AVAILABLE_STOCK_WITH_INCOMING"),
				},
				hasDefaultOptions: false,
			},
			{
				id: "in_stock_amount",
				name: t("enums.stocks.product_state_amount.IN_STOCK"),
				type: "number",
			},
			{
				id: "available_amount",
				name: t("enums.stocks.product_state_amount.AVAILABLE_STOCK"),
				type: "number",
			},
			{
				id: "available_amount_with_incoming",
				name: t("enums.stocks.product_state_amount.AVAILABLE_STOCK_WITH_INCOMING"),
				type: "number",
			},
		],
		segments: [
			{
				id: "all",
				name: t("common.word.all", { ns: "lib" }),
				slug: "all",
			},
		],
		selectedSegment: getSelectedSegmentForListConfig(data.segments, "all"),
		exportConfig: {
			title: t("modules.stock_state.field.export_config.title"),
			filename: t("modules.stock_state.header.title"),
			organization: organization.name,
		},
		fetch: async (params: Record<string, any> = {}, sourceToken?: CancelTokenSource) => {
			params.include = "custom_fields";
			return apiOrganization.getStockStates(params, { cancelToken: sourceToken?.token });
		},
		saveSegment: async (segment: SegmentType) => {
			const response = await segmentListService.save(listName, resourceType, segment);
			data.segments.push(response.data.data);

			return response;
		},
		numberOfStickyColumnsAtTheStart: 3,
		shouldDisableSortingOfStickyColumns: true,
	} as ListConfig;
	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...data.fields] : data.fields,
		customFields: data.fields,
		filterValues: data.filter_values,
	};

	const getColumnsFromFilters = (f: string) => {
		const decodedFilters = listFiltersDecode(f);
		const stockIdsFilter = decodedFilters.find((f) => f.filterId === "stockIds");
		let newColumns: StockApi[] = [];
		let values: string[] = [];
		if (stockIdsFilter) values = stockIdsFilter.value.split(",");

		if (values[0] === "a") newColumns = [...stocks];
		else if (values[0] === "u") newColumns = [];
		else newColumns = stocks.filter((stock) => values.includes(stock.id.toString()));

		return newColumns;
	};

	const getColumnsFromUrl = () => {
		const searchParams = new URLSearchParams(location.search);
		const filterColumnsFromUrl = searchParams.get("f");

		if (!filterColumnsFromUrl) {
			return [];
		}

		return getColumnsFromFilters(filterColumnsFromUrl);
	};

	const getColumnsFromSegment = () => {
		const { search } = location;
		const selectedSegmentId = search.split("s=")[1];
		const segment = data.segments.find((segment: { slug: string }) => segment.slug === selectedSegmentId);
		const segmentValue = segment?.value;
		const encodedFilterValue = listFiltersDecode(segmentValue).find(
			(filter: { filterId: string; value: string }) => filter.filterId === "f"
		)?.value;

		if (!encodedFilterValue || selectedSegmentId === "all") return [];
		return getColumnsFromFilters(encodedFilterValue);
	};

	const parseStockColumns = () => {
		let newColumns: StockApi[] = [];

		const columnsFromUrl = getColumnsFromUrl();
		const columnsFromSegment = getColumnsFromSegment();

		if (columnsFromUrl.length > 0) {
			newColumns = columnsFromUrl;
		} else if (columnsFromSegment.length > 0) {
			newColumns = columnsFromSegment;
		} else {
			newColumns = [...stocks];
		}
		return newColumns;
	};

	config = {
		...config,
		fields: [...defaultColumns, ...getStockColumns(parseStockColumns()), ...data.fields],
	};

	return config;
};

const List: FC<ListState> = ({ resource, stocks, handleCorrectStates }) => {
	const { t } = useTranslation();
	const location = useLocation();
	const segmentContextValue = useContext(SegmentContext);
	const organization = useSelector(selectOrganization);
	const isSupportRole = useSelector(hasRole(["ROLE_SUPPORT"]));

	const handleUpdateSegment = (segment: GoListSegmentType) => {
		data.segments[data.segments.findIndex(({ id }: { id: string }) => id === segment.id)] = { ...segment };
	};

	if (!resource) return null;

	const data = resource.read();
	const config = buildConfig(t, data, location, segmentContextValue, stocks, organization);

	const mobileActions: MobileActionProps[] = [
		{
			title: t("modules.stock_state.action.correct_states.title"),
			action: handleCorrectStates,
			hide: !isSupportRole,
		},
	];

	return <ListData config={config} onSegmentUpdate={handleUpdateSegment} mobileActions={mobileActions} />;
};

const OrganizationStockStateIndexPage = () => {
	const { t } = useTranslation();
	const [resource, setResource] = useState<Record<string, any>>();
	const [stocks, setStocks] = useState<StockApi[] | undefined>(undefined);
	const segmentContextValue = useContext(SegmentContext);
	const customFieldResourceTypes = [
		{
			type: "PRODUCT",
			name: t(`enums.custom_fields.resources.PRODUCT`),
		},
	];
	const { addFlash } = useFlash();
	const isMobile = useWindowSize().isMobile;
	const { handleChangeTabTitle } = useBrowserTabTitle();
	const isSupportRole = useSelector(hasRole(["ROLE_SUPPORT"]));

	useEffect(() => {
		onFetchStocks();
	}, []);

	useEffect(() => {
		handleChangeTabTitle(t("modules.stock_state.header.title"));
		setResource(wrapPromise(segmentContextValue.get(listName, resourceType, customFieldResourceTypes)));
	}, []);

	const onFetchStocks = async () => {
		try {
			const res = await apiOrganization.getStocks({ size: 0 });
			setStocks(res.data);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const handleCorrectStates = async () => {
		try {
			await apiOrganization.correctStates();
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const buttons: ButtonProps[] = [
		{
			title: t("modules.stock_state.action.correct_states.title"),
			dropdown: true,
			action: handleCorrectStates,
			hide: !isSupportRole,
		},
	];

	if (!stocks) return <></>;

	return (
		<>
			{!isMobile && <Header title={t("modules.stock_state.header.title")} buttons={buttons} />}
			<Suspense fallback={<LoadingContainer />}>
				<List resource={resource} stocks={stocks} handleCorrectStates={handleCorrectStates} />
			</Suspense>
		</>
	);
};

export default OrganizationStockStateIndexPage;
